import { useEffect, useCallback } from 'react';
import { Activity, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { SiteCard } from '../monitor/SiteCard';
import { AddSiteDialog } from '../monitor/AddSiteDialog';
import { EditSiteDialog } from '../monitor/EditSiteDialog';
import { DeleteSiteDialog } from '../monitor/DeleteSiteDialog';
import { SiteHistoryDialog } from '../monitor/SiteHistoryDialog';
import { useMonitor } from '../../hooks/useMonitor';
import { useMonitorWebSocket } from '../../hooks/useMonitorWebSocket';
import { useToast } from '../../contexts/ToastContext';
import type { WebSocketCheckMessage } from '../../types';

interface MonitorPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
}

export function MonitorPage({ accessToken, refreshToken, attemptRefresh }: MonitorPageProps) {
  const monitor = useMonitor({ accessToken, refreshToken, attemptRefresh });
  const toast = useToast();

  // Fetch sites on mount
  useEffect(() => {
    void monitor.fetchSites();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      void monitor.fetchSites();
    }, 30000);

    return () => clearInterval(interval);
  }, [monitor.fetchSites]);

  // WebSocket handler
  const handleWebSocketMessage = useCallback((message: WebSocketCheckMessage) => {
    // Update the site in the list
    monitor.updateSiteFromWebSocket(message.siteId, {
      id: '', // Will be ignored in the update
      siteId: message.siteId,
      status: message.status,
      responseTimeMs: message.responseTimeMs,
      issues: message.issues,
      checkedAt: message.checkedAt,
      httpStatus: undefined,
      sslValid: undefined
    });
  }, [monitor]);

  // WebSocket connection
  useMonitorWebSocket({
    accessToken: accessToken || null,
    enabled: !!accessToken,
    onCheckResult: handleWebSocketMessage
  });

  const handleAddSite = useCallback(async (data: { name: string; url: string; expectedContent?: string }) => {
    const result = await monitor.createSite(data);
    if (result.success) {
      toast.success('Site adicionado com sucesso');
    } else {
      toast.error(result.error || 'Erro ao adicionar site');
    }
  }, [monitor, toast]);

  const handleEditSite = useCallback(async (
    id: string,
    data: { name?: string; url?: string; expectedContent?: string; enabled?: boolean }
  ) => {
    const result = await monitor.updateSite(id, data);
    if (result.success) {
      toast.success('Site atualizado com sucesso');
    } else {
      toast.error(result.error || 'Erro ao atualizar site');
    }
  }, [monitor, toast]);

  const handleDeleteSite = useCallback(async (id: string) => {
    const result = await monitor.deleteSite(id);
    if (result.success) {
      toast.success('Site excluído com sucesso');
    } else {
      toast.error(result.error || 'Erro ao excluir site');
    }
  }, [monitor, toast]);

  const handleCheckNow = useCallback(async (id: string) => {
    const result = await monitor.triggerCheck(id);
    if (result.success) {
      toast.success('Verificação iniciada');
    } else {
      toast.error(result.error || 'Erro ao verificar site');
    }
  }, [monitor, toast]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Monitor de Sites</h1>
            <p className="text-sm text-muted-foreground">
              Acompanhe o status dos seus sites em tempo real
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => monitor.fetchSites()}
            disabled={monitor.loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${monitor.loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={monitor.openAddDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Site
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {monitor.loading && monitor.sites.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Carregando sites...</p>
          </div>
        </div>
      ) : monitor.sites.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Activity className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Nenhum site configurado</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            Adicione seu primeiro site para começar a monitorar a disponibilidade e desempenho em tempo real.
          </p>
          <Button onClick={monitor.openAddDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Primeiro Site
          </Button>
        </div>
      ) : (
        /* Sites Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {monitor.sites.map((site) => (
            <SiteCard
              key={site.id}
              site={site}
              isChecking={monitor.checkingId === site.id}
              onEdit={monitor.openEditDialog}
              onDelete={monitor.openDeleteDialog}
              onCheckNow={handleCheckNow}
              onViewHistory={monitor.openHistoryDialog}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <AddSiteDialog
        open={monitor.addDialogOpen}
        onOpenChange={monitor.setAddDialogOpen}
        onSubmit={handleAddSite}
      />

      <EditSiteDialog
        open={monitor.editDialogOpen}
        onOpenChange={monitor.setEditDialogOpen}
        site={monitor.selectedSite}
        onSubmit={handleEditSite}
      />

      <DeleteSiteDialog
        open={monitor.deleteDialogOpen}
        onOpenChange={monitor.setDeleteDialogOpen}
        site={monitor.selectedSite}
        onConfirm={handleDeleteSite}
      />

      <SiteHistoryDialog
        open={monitor.historyDialogOpen}
        onOpenChange={monitor.setHistoryDialogOpen}
        site={monitor.selectedSite}
        results={monitor.historyResults}
        uptimePercentage={monitor.uptimePercentage}
        avgResponseTime={monitor.avgResponseTime}
        loading={monitor.historyLoading}
      />
    </div>
  );
}
